import { BadgePrimitive, ButtonPrimitive, CardPrimitive } from '@figma/fpl-components';
import { borderRadius, color as fplColor, spacer } from '@figma/fpl-tokens/tokens.stylex';
import { Typography, type SxProps } from '@mui/material';
import * as stylex from '@stylexjs/stylex';
import { motion, type MotionProps } from 'motion/react';
import { forwardRef, useId, type KeyboardEvent, type MouseEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { I18N_KEYS } from '@/language/keys';
import { typographyStyles } from '@/UI/AppTypography/AppTypography';
import { XIcon } from '../Icons';

export type TagProps = Pick<MotionProps, 'transition' | 'layout' | 'layoutId'> & {
  text: string;
  bgColor: string;
  textColor: string;
  minWidth?: number;
  variant?: 'small' | 'large';
  endIcon?: ReactNode;
  sx?: SxProps;
  disableTextTransform?: boolean;
  isClearable?: boolean;
  onClick?: () => void;
  onClear?: () => void;
};

const styles = stylex.create({
  motionRoot: {
    display: 'inline-flex',
    maxWidth: '100%',
    width: 'fit-content',
  },
  base: {
    alignItems: 'center',
    borderRadius: borderRadius.medium,
    boxSizing: 'border-box',
    display: 'inline-flex',
    justifyContent: 'center',
    maxWidth: '100%',
    overflow: 'hidden',
    textTransform: 'capitalize',
    userSelect: 'none',
    whiteSpace: 'nowrap',
    width: 'fit-content',
  },
  preserveCasing: {
    textTransform: 'none',
  },
  passiveSmall: {
    height: '16px',
    paddingInline: spacer.spacer4px,
  },
  passiveLarge: {
    height: '20px',
    paddingInline: '6px',
  },
  interactive: {
    backgroundImage: {
      default: 'none',
      ':hover': `linear-gradient(${fplColor.bgTransparentHover}, ${fplColor.bgTransparentHover})`,
      ':active': `linear-gradient(${fplColor.bgTransparentPressed}, ${fplColor.bgTransparentPressed})`,
    },
    boxShadow: `inset 0 0 0 1px ${fplColor.border}`,
    cursor: 'pointer',
    isolation: 'isolate',
    outline: {
      default: 'none',
      ':has(:focus-visible)': `1px solid ${fplColor.borderSelected}`,
    },
    outlineOffset: {
      default: 0,
      ':has(:focus-visible)': '1px',
    },
  },
  interactiveSmall: {
    borderRadius: '3px',
    height: '20px',
  },
  interactiveLarge: {
    height: '24px',
  },
  productColors: (backgroundColor: string, color: string) => ({
    backgroundColor,
    color,
  }),
  minimumWidth: (minWidth: number) => ({
    minWidth,
  }),
  content: {
    alignItems: 'center',
    display: 'flex',
    height: '100%',
    minWidth: 0,
    width: '100%',
  },
  passiveText: {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  interactiveText: {
    flex: '1 1 auto',
    minWidth: 0,
    overflow: 'hidden',
    paddingInline: spacer.spacer4px,
    textOverflow: 'ellipsis',
  },
  interactiveTextLarge: {
    paddingInline: spacer.spacer8px,
  },
  mainButton: {
    borderRadius: 'inherit',
    outline: 'none',
  },
  trailing: {
    alignItems: 'center',
    display: 'inline-flex',
    flex: '0 0 auto',
    justifyContent: 'center',
  },
  closeButtonWrapper: {
    alignItems: 'center',
    display: 'flex',
    flex: '0 0 auto',
    height: '100%',
    justifyContent: 'center',
    overflow: 'hidden',
    position: 'relative',
  },
  closeButton: {
    alignItems: 'center',
    color: 'inherit',
    display: 'flex',
    height: '100%',
    justifyContent: 'center',
    opacity: {
      default: 1,
      ':active': 0.5,
    },
    outline: 'none',
    width: '20px',
  },
  closeButtonLarge: {
    width: '24px',
  },
});

// @@spec-file: ./Tag.spec.md
export const Tag = forwardRef<HTMLDivElement, TagProps>(function Tag(
  {
    text,
    bgColor,
    textColor,
    minWidth,
    variant = 'small',
    sx,
    endIcon,
    disableTextTransform = false,
    isClearable = false,
    onClick,
    onClear,
    transition,
    layout,
    layoutId,
  }: TagProps,
  ref,
) {
  const isFplMigrationEnabled = useFplMigrationFlag();
  const titleId = useId();
  const { t } = useTranslation();

  if (isFplMigrationEnabled === true) {
    const isInteractive = onClick != null || isClearable;
    const motionProps = { transition, layout, layoutId };
    const minimumWidthStyle = minWidth == null ? null : styles.minimumWidth(minWidth);
    const casingStyle = disableTextTransform ? styles.preserveCasing : null;

    if (!isInteractive) {
      return (
        <motion.div ref={ref} {...motionProps} {...stylex.props(styles.motionRoot)}>
          <BadgePrimitive
            {...stylex.props(
              styles.base,
              variant === 'large' ? styles.passiveLarge : styles.passiveSmall,
              variant === 'large' ? typographyStyles.textBodyLarge : typographyStyles.textBodyMedium,
              styles.productColors(bgColor, textColor),
              minimumWidthStyle,
              casingStyle,
            )}
          >
            <span {...stylex.props(styles.passiveText)}>{text}</span>
            {endIcon ? <span {...stylex.props(styles.trailing)}>{endIcon}</span> : null}
          </BadgePrimitive>
        </motion.div>
      );
    }

    const handleClearClick = (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      onClear?.();
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
      if (isClearable && (event.key === 'Backspace' || event.key === 'Delete')) {
        event.preventDefault();
        event.stopPropagation();
        onClear?.();
      }
    };

    return (
      <motion.div ref={ref} {...motionProps} {...stylex.props(styles.motionRoot)}>
        <CardPrimitive.Root
          onKeyDown={handleKeyDown}
          {...stylex.props(
            styles.base,
            styles.interactive,
            typographyStyles.textBodyMedium,
            variant === 'large' ? styles.interactiveLarge : styles.interactiveSmall,
            styles.productColors(bgColor, textColor),
            minimumWidthStyle,
            casingStyle,
          )}
        >
          <CardPrimitive.MainButton
            aria-description={
              isClearable ? t(I18N_KEYS.GENERAL.ARIA.REMOVE_NAMED_ITEM_KEYBOARD, { name: text }) : undefined
            }
            onClick={onClick}
            {...stylex.props(styles.mainButton)}
          />
          <div {...stylex.props(styles.content)}>
            <CardPrimitive.Title
              as="span"
              id={titleId}
              {...stylex.props(styles.interactiveText, variant === 'large' && styles.interactiveTextLarge)}
            >
              {text}
            </CardPrimitive.Title>
            {isClearable ? (
              <CardPrimitive.Interactive {...stylex.props(styles.closeButtonWrapper)}>
                <ButtonPrimitive
                  aria-describedby={titleId}
                  aria-label={t(I18N_KEYS.GENERAL.ARIA.REMOVE_NAMED_ITEM, { name: text })}
                  onClick={handleClearClick}
                  tabIndex={-1}
                  {...stylex.props(styles.closeButton, variant === 'large' && styles.closeButtonLarge)}
                >
                  <XIcon width={16} height={16} />
                </ButtonPrimitive>
              </CardPrimitive.Interactive>
            ) : endIcon ? (
              <span {...stylex.props(styles.trailing)}>{endIcon}</span>
            ) : null}
          </div>
        </CardPrimitive.Root>
      </motion.div>
    );
  }

  return (
    <Typography
      variant={variant === 'large' ? 'label-sm-rg' : 'label-xs-rg'}
      textTransform={disableTextTransform ? 'none' : 'capitalize'}
      color={textColor}
      ref={ref}
      component={motion.div}
      onClick={onClick}
      transition={transition}
      layout={layout}
      layoutId={layoutId}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: variant === 'large' ? 16 : 'unset',
        minWidth,
        background: bgColor,
        borderRadius: 0.5,
        px: 0.5,
        width: 'fit-content',
        gap: 0.5,
        ...sx,
      }}
    >
      {text}
      {isClearable ? (
        <XIcon
          width={12}
          height={12}
          style={{ cursor: 'pointer', flexShrink: 0 }}
          onClick={(e) => {
            e.stopPropagation();
            onClear?.();
          }}
        />
      ) : (
        endIcon
      )}
    </Typography>
  );
});
